
import React from "react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis
} from "@/components/ui/pagination";

interface JobPaginationProps {
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;
}

const JobPagination = ({ 
  currentPage, 
  totalPages, 
  goToPage
}: JobPaginationProps) => {
  if (totalPages <= 1) return null;

  // Calculate which page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Maximum number of page links to show
    
    // Always show first page
    pageNumbers.push(1);
    
    if (totalPages <= maxVisiblePages) {
      // If we have fewer pages than max visible, show all of them
      for (let i = 2; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // We need to determine which pages to show and where to put ellipses
      
      // Show pages around the current page
      const leftBound = Math.max(2, currentPage - 1);
      const rightBound = Math.min(totalPages - 1, currentPage + 1);
      
      // Add ellipsis before if needed
      if (leftBound > 2) {
        pageNumbers.push('ellipsis-left');
      }
      
      // Add page numbers around current page
      for (let i = leftBound; i <= rightBound; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis after if needed
      if (rightBound < totalPages - 1) {
        pageNumbers.push('ellipsis-right');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };
  
  const pageElements = getPageNumbers();
  
  return (
    <Pagination className="mt-8">
      <PaginationContent>
        {currentPage > 1 && (
          <PaginationItem>
            <PaginationPrevious 
              onClick={() => goToPage(currentPage - 1)} 
              className="cursor-pointer font-display"
            />
          </PaginationItem>
        )}
        
        {pageElements.map((pageElement, index) => {
          if (pageElement === 'ellipsis-left' || pageElement === 'ellipsis-right') {
            return (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }
          
          const pageNumber = pageElement as number;
          
          return (
            <PaginationItem key={pageNumber}>
              <PaginationLink 
                onClick={() => goToPage(pageNumber)}
                isActive={pageNumber === currentPage}
                className="cursor-pointer font-display"
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          );
        })}
        
        {currentPage < totalPages && (
          <PaginationItem>
            <PaginationNext 
              onClick={() => goToPage(currentPage + 1)} 
              className="cursor-pointer font-display"
            />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
};

export default JobPagination;
